import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import EventCard from './components/EventCard';
import FeaturedEvent from './components/FeaturedEvent';
import EventDetail from './components/EventDetail';
import TravelPage from './pages/TravelPage';
import SubmitEventPage from './pages/SubmitEventPage';
import SchedulePage from './pages/SchedulePage';
import SpeakersPage from './pages/SpeakersPage';
import SpeakerDetail from './components/SpeakerDetail';
import SpeakerApplyPage from './pages/SpeakerApplyPage';
import VenuePage from './pages/VenuePage';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';
import RegistrationPage from './pages/RegistrationPage';
import SupportPage from './pages/SupportPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CodeOfConductPage from './pages/CodeOfConductPage';
import Footer from './components/Footer';
import InstallPrompt from './components/InstallPrompt';
import MobileTabBar from './components/MobileTabBar';
import MobileHome from './components/MobileHome';
import { MobileDiscover, MobileSchedule, MobileTickets, MobileProfile, MobilePageTransition, MobileLogin, MobileSignup, MobileForgotPassword, MobileSpeakerDetail, MobileTravel, MobileVenue, MobileRegistration } from './components/mobile';
import { isNativePlatform } from './native/platform';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import PendingEventsPage from './pages/admin/PendingEventsPage';
import AdminEventsPage from './pages/admin/AdminEventsPage';
import AdminSpeakersPage from './pages/admin/AdminSpeakersPage';
import CreateEventPage from './pages/admin/CreateEventPage';
import CreateSpeakerPage from './pages/admin/CreateSpeakerPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminTestPage from './pages/admin/AdminTestPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import AdminRemindersPage from './pages/admin/AdminRemindersPage';
import AdminEventRemindersPage from './pages/admin/AdminEventRemindersPage';
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage';
import AdminRegistrationsPage from './pages/admin/AdminRegistrationsPage';
import SpeakerLayout from './pages/speaker/SpeakerLayout';
import SpeakerDashboardNew from './pages/speaker/SpeakerDashboardNew';
import SpeakerEventsPage from './pages/speaker/SpeakerEventsPage';
import SpeakerSubmissionsPage from './pages/speaker/SpeakerSubmissionsPage';
import SpeakerAnalyticsPage from './pages/speaker/SpeakerAnalyticsPage';
import SpeakerMediaPage from './pages/speaker/SpeakerMediaPage';
import SpeakerProfilePage from './pages/speaker/SpeakerProfilePage';
import ChurchLayout from './pages/church/ChurchLayout';
import ChurchDashboardNew from './pages/church/ChurchDashboardNew';
import ChurchEventsPage from './pages/church/ChurchEventsPage';
import ChurchCreateEventPage from './pages/church/ChurchCreateEventPage';
import ChurchRegistrationsPage from './pages/church/ChurchRegistrationsPage';
import ChurchAnalyticsPage from './pages/church/ChurchAnalyticsPageNew';
import ChurchPaymentsPage from './pages/church/ChurchPaymentsPage';
import ChurchSettingsPage from './pages/church/ChurchSettingsPage';
import AttendeeLayout from './pages/attendee/AttendeeLayout';
import AttendeeDashboard from './pages/attendee/AttendeeDashboard';
import AttendeeHome from './pages/attendee/AttendeeHome';
import MyEventsPage from './pages/attendee/MyEventsPage';
import EventHub from './pages/attendee/EventHub';
import AttendeeTicketsPage from './pages/attendee/AttendeeTicketsPage';
import AttendeeProfilePage from './pages/attendee/AttendeeProfilePage';
import AttendeeNotificationsPage from './pages/attendee/AttendeeNotificationsPage';
import AttendeeSchedulePage from './pages/attendee/AttendeeSchedulePage';
import MyTicketsPage from './pages/attendee/MyTicketsPage';
import SettingsPage from './pages/SettingsPage';
import CheckInPage from './pages/CheckInPage';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AdminPasswordReset from './pages/auth/AdminPasswordReset';
import AuthCallbackPage from './pages/auth/AuthCallbackPage';
import ImpersonatePage from './pages/auth/ImpersonatePage';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import { useEvents } from './hooks/useEvents';
import { usePushNotifications } from './hooks/usePushNotifications';
import { supabase, updateExpiredEvents } from './lib/supabase';
import AuthGuard from './components/AuthGuard';
import './App.css';
import { useStatusBar } from './native/statusBar';

// Registers the device for native push notifications once authenticated (no-op on web).
const PushNotificationsManager = () => {
  usePushNotifications();
  return null;
};

// Sets the native status bar style (no-op on web).
const StatusBarManager = () => {
  useStatusBar();
  return null;
};

// Helper function to check if an event is in the past
const isPastEvent = (event) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = new Date(event.end || event.start || event.date);
  return eventDate < today;
};

// Events List Component
const EventsList = ({ events, savedEvents, onSaveToggle, showPastEvents = false, showHeader = false }) => {
  // Filter events based on whether they're past or upcoming
  const filteredEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const result = events.filter(event => {
      // Always show cancelled events in upcoming view
      if (event.status === 'cancelled' && !showPastEvents) {
        return true;
      }
      
      const eventDate = new Date(event.end || event.start || event.date);
      return showPastEvents ? eventDate < today : eventDate >= today;
    }).sort((a, b) => {
      const dateA = new Date(a.start || a.date);
      const dateB = new Date(b.start || b.date);
      return showPastEvents 
        ? dateB - dateA // Most recent past events first
        : dateA - dateB; // Upcoming events in chronological order
    });
    
    return result;
  }, [events, showPastEvents]);

  if (filteredEvents.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-12">
          <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            {showPastEvents 
              ? "No past events found." 
              : "No upcoming events scheduled. Please check back later!"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <section className="bg-gray-50 dark:bg-gray-900 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {showHeader && (
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
              {showPastEvents ? 'Archive' : 'Don\'t Miss Out'}
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {showPastEvents ? 'Past Events' : 'Upcoming Events'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {showPastEvents 
                ? 'Browse through our archive of past conferences and events.'
                : 'Discover and plan your attendance at upcoming conferences across the country.'}
            </p>
          </div>
        )}
        
        <div className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {filteredEvents.map((event, index) => (
            <div 
              key={event.id} 
              className="animate-slide-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <EventCard 
                event={{
                  id: event.id,
                  title: event.event,
                  date: event.start,
                  city: event.city,
                  venue: event.venue,
                  location: `${event.venue}, ${event.city}`,
                  organization: event.org,
                  org: event.org,
                  image: event.image,
                  image_url: event.image_url,
                  status: event.status,
                  hotels: event.hotels,
                  airport: event.airport,
                  notes: event.notes,
                  start: event.start,
                  end: event.end,
                  description: event.description,
                  event_type: event.event_type,
                  event_audience: event.event_audience,
                  event_category: event.event_category
                }}
                isSaved={savedEvents.includes(event.id)}
                onSaveToggle={() => onSaveToggle(event.id)}
              />
            </div>
          ))}
        </div>
        
        {!showPastEvents && filteredEvents.length > 0 && (
          <div className="text-center mt-12">
            <a 
              href="/events" 
              className="inline-flex items-center px-6 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-xl transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
            >
              View All Events
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

const App = () => {
  console.log('App component rendering');
  const { events: supabaseEvents, loading: eventsLoading, error: eventsError, refetch } = useEvents();
  console.log('useEvents result:', { events: supabaseEvents?.length, loading: eventsLoading, error: eventsError });
  
    
  const [savedEvents, setSavedEvents] = useState(() => {
    const saved = localStorage.getItem('savedEvents');
    return saved ? JSON.parse(saved) : [];
  });

  // Use events from database only
  const events = supabaseEvents;

  // Save to localStorage when savedEvents changes
  useEffect(() => {
    localStorage.setItem('savedEvents', JSON.stringify(savedEvents));
  }, [savedEvents]);

  const handleSaveToggle = (eventId) => {
    setSavedEvents(prev => 
      prev.includes(eventId) 
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  };

  // Update expired events on app load (runs once per session)
  useEffect(() => {
    const updateEventStatuses = async () => {
      try {
        const { data, error } = await updateExpiredEvents();
        if (error) {
          console.log('Event status update skipped:', error.message);
        } else if (data && (data[0]?.ongoing_updated > 0 || data[0]?.past_updated > 0)) {
          console.log('Event statuses updated:', data[0]);
          refetch(); // Refresh events after status update
        }
      } catch (err) {
        // Silently fail - function may not exist yet
        console.log('Event status update not available');
      }
    };
    updateEventStatuses();
  }, [refetch]);

  
  return (
    <AuthProvider>
      <Router>
        <AuthGuard>
          <ScrollToTop />
          <InstallPrompt />
          <PushNotificationsManager />
          <StatusBarManager />
          <MobileTabBar />
          <Routes>
          {/* Auth Routes - No navigation */}
          <Route path="/login" element={isNativePlatform() ? <MobileLogin /> : <LoginPage />} />
          <Route path="/signup" element={isNativePlatform() ? <MobileSignup /> : <SignupPage />} />
          <Route path="/forgot-password" element={isNativePlatform() ? <MobileForgotPassword /> : <ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/admin-reset" element={<AdminPasswordReset />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/impersonate" element={<ImpersonatePage />} />

          {/* Event Check-In (organizers/admins) - mobile QR scanner */}
          <Route path="/check-in" element={
            <ProtectedRoute requireChurchAdmin>
              <CheckInPage />
            </ProtectedRoute>
          } />

          {/* Speaker Portal - Protected */}
          <Route path="/speaker" element={
            <ProtectedRoute>
              <SpeakerLayout />
            </ProtectedRoute>
          }>
            <Route index element={<SpeakerDashboardNew />} />
            <Route path="events" element={<SpeakerEventsPage />} />
            <Route path="submissions" element={<SpeakerSubmissionsPage />} />
            <Route path="analytics" element={<SpeakerAnalyticsPage />} />
            <Route path="media" element={<SpeakerMediaPage />} />
            <Route path="profile" element={<SpeakerProfilePage />} />
          </Route>

          {/* Legacy speaker dashboard redirect */}
          <Route path="/speaker-dashboard" element={
            <ProtectedRoute>
              <SpeakerLayout />
            </ProtectedRoute>
          }>
            <Route index element={<SpeakerDashboardNew />} />
          </Route>

          {/* Church Admin Portal - Protected for church admins */}
          <Route path="/church" element={
            <ProtectedRoute requireChurchAdmin>
              <ChurchLayout />
            </ProtectedRoute>
          }>
            <Route index element={<ChurchDashboardNew />} />
            <Route path="events" element={<ChurchEventsPage />} />
            <Route path="events/new" element={<ChurchCreateEventPage />} />
            <Route path="events/:id/edit" element={<ChurchCreateEventPage />} />
            <Route path="registrations" element={<ChurchRegistrationsPage />} />
            <Route path="analytics" element={<ChurchAnalyticsPage />} />
            <Route path="payments" element={<ChurchPaymentsPage />} />
            <Route path="settings" element={<ChurchSettingsPage />} />
          </Route>

          {/* Legacy church dashboard redirect */}
          <Route path="/church-dashboard" element={
            <ProtectedRoute requireChurchAdmin>
              <ChurchLayout />
            </ProtectedRoute>
          }>
            <Route index element={<ChurchDashboardNew />} />
          </Route>

          {/* Attendee Portal - Protected */}
          {/* New journey-oriented attendee routes */}
          <Route path="/attendee" element={
            <ProtectedRoute>
              {isNativePlatform() ? <MobilePageTransition><MobileProfile /></MobilePageTransition> : <AttendeeLayout />}
            </ProtectedRoute>
          }>
            {!isNativePlatform() && <Route index element={<AttendeeHome />} />}
          </Route>

          <Route path="/attendee/events" element={
            <ProtectedRoute>
              {isNativePlatform() ? <MobilePageTransition><MobileProfile /></MobilePageTransition> : <AttendeeLayout />}
            </ProtectedRoute>
          }>
            {!isNativePlatform() && <Route index element={<MyEventsPage />} />}
          </Route>

          <Route path="/attendee/tickets" element={
            <ProtectedRoute>
              {isNativePlatform() ? <MobilePageTransition><MobileTickets /></MobilePageTransition> : <AttendeeLayout />}
            </ProtectedRoute>
          }>
            {!isNativePlatform() && <Route index element={<AttendeeTicketsPage />} />}
          </Route>

          <Route path="/attendee/notifications" element={
            <ProtectedRoute>
              {isNativePlatform() ? <MobilePageTransition><MobileProfile /></MobilePageTransition> : <AttendeeLayout />}
            </ProtectedRoute>
          }>
            {!isNativePlatform() && <Route index element={<AttendeeNotificationsPage />} />}
          </Route>

          <Route path="/attendee/profile" element={
            <ProtectedRoute>
              {isNativePlatform() ? <MobilePageTransition><MobileProfile /></MobilePageTransition> : <AttendeeLayout />}
            </ProtectedRoute>
          }>
            {!isNativePlatform() && <Route index element={<AttendeeProfilePage />} />}
          </Route>

          <Route path="/attendee/schedule" element={
            <ProtectedRoute>
              {isNativePlatform() ? <MobilePageTransition><MobileProfile /></MobilePageTransition> : <AttendeeLayout />}
            </ProtectedRoute>
          }>
            {!isNativePlatform() && <Route index element={<AttendeeSchedulePage />} />}
          </Route>

          <Route path="/my-events/:eventId" element={
            <ProtectedRoute>
              {isNativePlatform() ? <MobilePageTransition><MobileProfile /></MobilePageTransition> : <AttendeeLayout />}
            </ProtectedRoute>
          }>
            {!isNativePlatform() && <Route index element={<EventHub />} />}
          </Route>

          {/* Legacy attendee routes - redirect to new routes */}
          <Route path="/my-profile" element={
            <ProtectedRoute>
              {isNativePlatform() ? <MobilePageTransition><MobileProfile /></MobilePageTransition> : <AttendeeLayout />}
            </ProtectedRoute>
          }>
            {!isNativePlatform() && <Route index element={<AttendeeHome />} />}
          </Route>

          <Route path="/my-tickets" element={
            <ProtectedRoute>
              {isNativePlatform() ? <MobilePageTransition><MobileTickets /></MobilePageTransition> : <AttendeeLayout />}
            </ProtectedRoute>
          }>
            {!isNativePlatform() && <Route index element={<AttendeeTicketsPage />} />}
          </Route>

          <Route path="/settings" element={
            <ProtectedRoute>
              <AttendeeLayout />
            </ProtectedRoute>
          }>
            <Route index element={<SettingsPage />} />
          </Route>

          {/* Admin Routes - Protected, separate layout */}
          <Route path="/admin" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="events" element={<AdminEventsPage />} />
            <Route path="events/new" element={<CreateEventPage />} />
            <Route path="events/pending" element={<PendingEventsPage />} />
            <Route path="speakers" element={<AdminSpeakersPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="test" element={<AdminTestPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
            <Route path="reminders" element={<AdminRemindersPage />} />
            <Route path="event-reminders" element={<AdminEventRemindersPage />} />
            <Route path="notifications" element={<AdminNotificationsPage />} />
            <Route path="registrations" element={<AdminRegistrationsPage />} />
          </Route>

        {/* Main Site Routes */}
        <Route path="/*" element={
          <div className="app-container">
            <Navigation />
            <main className="flex-grow">
              <Routes>
            <Route 
              path="/" 
              element={
                isNativePlatform() ? (
                  <MobilePageTransition>
                    <MobileHome events={events} savedEvents={savedEvents} onSaveToggle={handleSaveToggle} />
                  </MobilePageTransition>
                ) : (
                <>
                  <Hero />
                  <FeaturedEvent 
                    event={events.find(event => event.featured) || events[0]} 
                  />
                  <EventsList 
                    events={events} 
                    savedEvents={savedEvents} 
                    onSaveToggle={handleSaveToggle}
                    showHeader={true}
                  />
                </>
                )
              } 
            />
            <Route 
              path="/events" 
              element={
                isNativePlatform() ? (
                  <MobilePageTransition>
                    <MobileDiscover events={events} savedEvents={savedEvents} onSaveToggle={handleSaveToggle} />
                  </MobilePageTransition>
                ) : (
                <>
                  <div className="bg-gradient-to-br from-primary-dark to-primary pt-32 pb-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-2">Upcoming Events</h1>
                          <p className="text-white/70">Find your next conference experience</p>
                        </div>
                        <a 
                          href="/events/past" 
                          className="inline-flex items-center px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-lg transition-all"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          View Past Events
                        </a>
                      </div>
                    </div>
                  </div>
                  <EventsList 
                    events={events} 
                    savedEvents={savedEvents} 
                    onSaveToggle={handleSaveToggle} 
                    showPastEvents={false}
                  />
                </>
                )
              } 
            />
            <Route 
              path="/events/past" 
              element={
                <>
                  <div className="bg-white py-8 border-b border-gray-200">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                      <div className="flex justify-between items-center">
                        <h1 className="text-3xl font-bold text-gray-900">Past Events</h1>
                        <a 
                          href="/events" 
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          ← Back to Upcoming Events
                        </a>
                      </div>
                    </div>
                  </div>
                  <EventsList 
                    events={events} 
                    savedEvents={savedEvents} 
                    onSaveToggle={handleSaveToggle} 
                    showPastEvents={true}
                  />
                </>
              } 
            />
            <Route 
              path="/events/:id" 
              element={
                <EventDetail 
                  savedEvents={savedEvents} 
                  onSaveToggle={handleSaveToggle} 
                />
              } 
            />
            <Route 
              path="/travel" 
              element={
                isNativePlatform() ? (
                  <MobilePageTransition>
                    <MobileTravel />
                  </MobilePageTransition>
                ) : (
                  <TravelPage />
                )
              } 
            />
            <Route 
              path="/venue" 
              element={
                isNativePlatform() ? (
                  <MobilePageTransition>
                    <MobileVenue />
                  </MobilePageTransition>
                ) : (
                  <VenuePage />
                )
              } 
            />
            <Route 
              path="/about" 
              element={
                <AboutPage />
              } 
            />
            <Route 
              path="/faq" 
              element={
                <FAQPage />
              } 
            />
            <Route 
              path="/support" 
              element={
                <SupportPage />
              } 
            />
            <Route 
              path="/contact" 
              element={
                <ContactPage />
              } 
            />
            <Route 
              path="/privacy" 
              element={
                <PrivacyPolicyPage />
              } 
            />
            <Route 
              path="/terms" 
              element={
                <TermsOfServicePage />
              } 
            />
            <Route 
              path="/code-of-conduct" 
              element={
                <CodeOfConductPage />
              } 
            />
            <Route 
              path="/register" 
              element={
                isNativePlatform() ? (
                  <MobilePageTransition>
                    <MobileRegistration />
                  </MobilePageTransition>
                ) : (
                  <RegistrationPage />
                )
              } 
            />
            <Route
              path="/saved"
              element={
                isNativePlatform() ? (
                  <MobilePageTransition>
                    <MobileHome events={events} savedEvents={savedEvents} onSaveToggle={handleSaveToggle} />
                  </MobilePageTransition>
                ) : (
                  <EventsList
                    events={events.filter(e => savedEvents.includes(e.id))}
                    savedEvents={savedEvents}
                    onSaveToggle={handleSaveToggle}
                    showHeader={false}
                  />
                )
              }
            />
            <Route 
              path="/schedule" 
              element={
                isNativePlatform() ? (
                  <MobilePageTransition>
                    <MobileSchedule events={events} />
                  </MobilePageTransition>
                ) : (
                  <SchedulePage />
                )
              } 
            />
            <Route 
              path="/submit-event" 
              element={
                <SubmitEventPage />
              } 
            />
            <Route 
              path="/speakers" 
              element={
                <SpeakersPage />
              } 
            />
            <Route 
              path="/speakers/apply" 
              element={
                <SpeakerApplyPage />
              } 
            />
            <Route 
              path="/speakers/:id" 
              element={
                isNativePlatform() ? (
                  <MobilePageTransition>
                    <MobileSpeakerDetail />
                  </MobilePageTransition>
                ) : (
                  <SpeakerDetail />
                )
              } 
            />
              </Routes>
            </main>
            {!isNativePlatform() && <Footer />}
          </div>
        } />
        </Routes>
        </AuthGuard>
      </Router>
    </AuthProvider>
  );
}

export default App;
